
import { supabase } from '@/integrations/supabase/client';
import { TrackDownload } from './types';

/**
 * Increments the download count for a track
 * @param trackId The ID of the track to increment download count for
 * @returns Promise that resolves with success status and error if any
 */
export async function incrementDownloadCount(trackId: string): Promise<{ success: boolean; error: Error | null }> {
  try {
    // Don't proceed if track ID is missing
    if (!trackId) {
      throw new Error("Cannot increment download count: Missing track ID");
    }
    
    // Call the increment function we created in SQL
    const { data, error } = await supabase.rpc('increment', { 
      row_id: trackId, 
      column_name: 'download_count' 
    });
    
    if (error) throw error;
    
    return { success: true, error: null };
  } catch (error) {
    console.error('Error incrementing download count:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error : new Error('Unknown error incrementing download count') 
    };
  }
}

/**
 * Gets download info for a track
 * @param trackId The ID of the track to get info for
 * @returns Promise that resolves with the track download info
 */
export async function getTrackDownload(trackId: string): Promise<TrackDownload | null> {
  try {
    // Validate the track ID before querying
    if (!trackId || trackId.trim() === '') {
      console.error('Invalid track ID provided');
      return null;
    }
    
    console.log("Fetching track download with ID:", trackId);
    
    // Use the public access policy to fetch track data
    // This function should work for both authenticated and unauthenticated users
    const { data, error } = await supabase
      .from('tracks')
      .select('*')
      .eq('id', trackId)
      .single();

    if (error) {
      console.error('Error fetching track download:', error);
      return null;
    }

    if (!data) {
      console.error('No track found with ID:', trackId);
      return null;
    }

    const trackDownload: TrackDownload = {
      id: data.id,
      trackId: data.track_id || '',
      spotifyUrl: data.spotify_url || '',
      artistId: data.artist_id,
      fileUrl: data.file_url,
      fileName: data.file_name,
      fileType: data.file_type,
      fileSize: data.file_size,
      downloadCount: data.download_count,
      createdAt: data.created_at,
      trackName: data.track_title,
      artistName: data.artist_name,
      description: data.description
    };

    return trackDownload;
  } catch (error) {
    console.error('Error in getTrackDownload:', error);
    return null;
  }
}
